import { patientPhotoUrl } from '../../shared/patient/patient-photo.ts';
import { PRACTICES } from './practices.service.ts';
import { getCurrentPracticeId } from './practices.service.ts';

export interface Patient {
  uuid: string;
  practiceId: string;
  name: string;
  age: number;
  sex: 'F' | 'M';
  phone: string;
  last: string;
  tag: string;
  color: string;
  photoUrl: string;
}

/** Nombre de patients mock par cabinet. */
export const MOCK_PATIENTS_PER_PRACTICE = 56;

const FEMALE_FIRST = [
  'Marie',
  'Sophie',
  'Élodie',
  'Camille',
  'Léa',
  'Anne',
  'Inès',
  'Nina',
  'Emma',
  'Chloé',
  'Lola',
  'Sarah',
  'Clara',
  'Manon',
  'Alice',
  'Mila',
  'Jeanne',
  'Juliette',
  'Margot',
  'Rose',
  'Ambre',
  'Lina',
  'Zoé',
  'Eva',
  'Anna',
  'Lucie',
  'Océane',
  'Jade',
  'Lou',
  'Charlotte',
  'Adèle',
];

const MALE_FIRST = [
  'Antoine',
  'Julien',
  'Marc',
  'Hugo',
  'Paul',
  'Lucas',
  'Adam',
  'Noah',
  'Mathis',
  'Ethan',
  'Tom',
  'Raphaël',
  'Nathan',
  'Yanis',
  'Oscar',
  'Gabriel',
  'Louis',
  'Arthur',
  'Jules',
  'Léo',
  'Maxime',
  'Théo',
  'Enzo',
  'Nolan',
  'Timéo',
  'Axel',
  'Victor',
  'Samuel',
  'Simon',
  'Baptiste',
];

const SURNAMES = [
  'Lefèvre',
  'Roux',
  'Bernard',
  'Petit',
  'Garcia',
  'Dubois',
  'Henry',
  'Moreau',
  'Martin',
  'Rousseau',
  'Lemaire',
  'Caron',
  'Faure',
  'Morel',
  'Laurent',
  'Perrin',
  'Garnier',
  'Mercier',
  'Girard',
  'Colin',
  'Renard',
  'Marchand',
  'Vidal',
  'Nguyen',
  'Meunier',
  'Simon',
  'Aubert',
  'Blanc',
  'Chevalier',
  'Roche',
  'Carpentier',
  'Lambert',
  'Roussel',
  'Masson',
  'Dumas',
  'Fontaine',
  'Barbier',
  'Muller',
  'Guérin',
  'Bonnet',
  'Fournier',
  'Giraud',
  'Leclerc',
  'Robin',
  'Morin',
  'Henry-Dupont',
  'Schmitt',
  'Martinez',
  'Lopez',
  'Klein',
  'André',
];

const TAGS = [
  'Multiattache',
  'Appareil fixe',
  'Aligneurs',
  'Premier RDV',
  'Bilan annuel',
  'Pose bagues',
  'Contention',
  'Premier contact',
  'Surléage',
  'Débondage',
  'Phase 2',
  'Interception',
  'Élastiques',
  'Expansion',
  'Finition',
  'Contrôle mensuel',
  'Bilan initial',
  'Aligneurs adulte',
  'Arc cassé',
  'Urgence',
];

const COLORS = [
  '#fb923c',
  '#60a5fa',
  '#a78bfa',
  '#34d399',
  '#f472b6',
  '#fbbf24',
  '#22d3ee',
  '#f87171',
  '#a3e635',
  '#c084fc',
  '#fb7185',
  '#38bdf8',
  '#f97316',
  '#0ea5e9',
  '#ec4899',
  '#8b5cf6',
  '#14b8a6',
  '#84cc16',
  '#f43f5e',
  '#6366f1',
  '#22c55e',
  '#06b6d4',
  '#d946ef',
  '#ef4444',
  '#7c3aed',
  '#ea580c',
  '#0891b2',
  '#e11d48',
  '#059669',
  '#475569',
];

const LAST_VISITS = [
  '20/04/2026',
  '15/04/2026',
  '27/04/2026',
  '10/04/2026',
  '02/04/2026',
  '22/04/2026',
  '18/04/2026',
  '12/04/2026',
  '08/04/2026',
  '06/05/2026',
  '30/04/2026',
  '28/04/2026',
  '05/05/2026',
  '11/05/2026',
  '04/05/2026',
  '29/04/2026',
  '13/05/2026',
  '07/05/2026',
  '12/05/2026',
  '03/05/2026',
  '09/05/2026',
  '10/05/2026',
  '02/05/2026',
  '14/05/2026',
  '08/05/2026',
  '16/05/2026',
  '01/05/2026',
  '—',
];

function patientUuid(practiceIndex: number, patientIndex: number): string {
  const n = practiceIndex * 1000 + patientIndex;
  const tail = n.toString(16).padStart(12, '0').slice(-12);
  return `a0000000-0000-4000-8000-${tail}`;
}

function phoneFor(practiceIndex: number, patientIndex: number): string {
  const a = (10 + ((practiceIndex * 7 + patientIndex) % 80)).toString().padStart(2, '0');
  const b = ((practiceIndex * 11 + patientIndex * 3) % 100).toString().padStart(2, '0');
  const c = ((practiceIndex * 13 + patientIndex * 5) % 100).toString().padStart(2, '0');
  const d = ((practiceIndex * 17 + patientIndex * 7) % 100).toString().padStart(2, '0');
  const e = ((practiceIndex * 19 + patientIndex * 9) % 100).toString().padStart(2, '0');
  return `0${a} ${b} ${c} ${d} ${e}`;
}

function buildMockPatients(perPractice = MOCK_PATIENTS_PER_PRACTICE): Patient[] {
  const patients: Patient[] = [];

  PRACTICES.forEach((practice, practiceIndex) => {
    for (let i = 0; i < perPractice; i++) {
      const female = i % 2 === 0;
      const first = female
        ? FEMALE_FIRST[(practiceIndex * 3 + i) % FEMALE_FIRST.length]!
        : MALE_FIRST[(practiceIndex * 5 + i) % MALE_FIRST.length]!;
      const last = SURNAMES[(practiceIndex + i * 2) % SURNAMES.length]!;
      const global = practiceIndex * perPractice + i;

      const uuid = patientUuid(practiceIndex, i);
      patients.push({
        uuid,
        practiceId: practice.uuid,
        name: `${first} ${last}`,
        age: 8 + ((global * 7) % 18),
        sex: female ? 'F' : 'M',
        phone: phoneFor(practiceIndex, i),
        last: LAST_VISITS[global % LAST_VISITS.length]!,
        tag: TAGS[global % TAGS.length]!,
        color: COLORS[global % COLORS.length]!,
        photoUrl: patientPhotoUrl(uuid),
      });
    }
  });

  return patients;
}

const MOCK_PATIENTS: Patient[] = buildMockPatients();

function filterByPractice(patients: Patient[], practiceId?: string): Patient[] {
  const id = practiceId ?? getCurrentPracticeId();
  return patients.filter((p) => p.practiceId === id);
}

/** Patient minimal pour les données mock locales (hors liste cabinet). */
export function stubPatient(
  patient: Omit<Patient, 'practiceId' | 'photoUrl'> &
    Partial<Pick<Patient, 'photoUrl'>> & { practiceId?: string },
): Patient {
  const { practiceId, photoUrl, ...rest } = patient;
  const uuid = rest.uuid;
  return {
    ...rest,
    practiceId: practiceId ?? getCurrentPracticeId(),
    photoUrl: photoUrl ?? patientPhotoUrl(uuid),
  };
}

export const patientsService = {
  list(practiceId?: string): Patient[] {
    return filterByPractice(MOCK_PATIENTS, practiceId);
  },

  findByUuid(uuid: string, practiceId?: string): Patient | undefined {
    return filterByPractice(MOCK_PATIENTS, practiceId).find(
      (p) => p.uuid === uuid,
    );
  },

  findByName(name: string, practiceId?: string): Patient | undefined {
    return filterByPractice(MOCK_PATIENTS, practiceId).find(
      (p) => p.name === name,
    );
  },
};
